/**
 * The cut-to-size modal: the customer picks a cut shape, process, finish, tolerances, dimensions
 * in inches and a piece count for one product, and on Apply the page receives a `Processing`
 * definition to store on the cart, list, quote or search line. This component holds all state and
 * rules; `DefineCutModalDesktop.tsx` (from the `lg` breakpoint) and `DefineCutModalMobile.tsx`
 * only render.
 *
 * Position:
 * - Rendered by `components/ShoppingCart/ShoppingCart.tsx`, `components/Catalog/CatalogItems.tsx`,
 *   `components/Search/Search.tsx`, `components/Product/ProductDetails.tsx`,
 *   `components/ShoppingListDetails/ShoppingListDetails.tsx` and
 *   `components/QuoteDetails/QuoteDetails.tsx`. Each passes `onSuccessUpdate`, which stores the
 *   cut on its line; the cart, search, list and quote handlers act only when
 *   `isResponseUpdated` is true (the cart, for example, then updates the Shopping API line or the
 *   guest cart, with quantity in pieces).
 * - Reads the product's cut options from the Items product-details route
 *   (`APIUrl.PRODUCT_DETAILS`): `processingAttribute[<locale>]` lists cut shapes, processes and
 *   finishes, and `tolerances` lists tolerances per process code. The product details page
 *   already has that object and passes `requireProductDetailForProcessing={false}`.
 * - Which fields appear comes from `@ryerson/common.process-cut` through
 *   `DefineCutHelper.ts` → `getCutAttributes`.
 *
 * Flow:
 * 1. Opening: fetch product details once per product, then either load `defineCutData` (editing
 *    an existing cut) or apply defaults (new cut: `RECTANGLE` for plate, otherwise the first cut
 *    shape the API lists; the first process).
 * 2. Changing the cut shape rebuilds the field list and resets the form; changing the process
 *    resets the tolerances to that process's default and may lock width or length to stock size.
 * 3. Apply validates (`validateCutParameters`), requires a quantity above 0, and calls
 *    `onSuccessUpdate` with a {@link DefineCutResponse}. No API request is made here; the page
 *    stores the cut. The GA `APPLY` event is sent.
 *
 * Rules a change must keep:
 * - When the product has no processing attributes for the user's locale, `productCanBeCut` is
 *   false and both views show the "cannot be cut, contact us" message instead of the form.
 * - The dimension rules live in `validateCutParameters` and `DefineCutHelper.ts` →
 *   `hasValidProperties`; their limits are repeated in the error text of
 *   `DefineCutModalContent.ts`.
 * - Choosing a shear process (`SHR`, `STW`, `STL`) disables the circle and ring cut shapes, and
 *   choosing a circle or ring disables the shear processes. The desktop view drops disabled
 *   options from its dropdowns; the mobile view passes them with `disabled` set.
 * - Google Analytics `CUT_TO_SIZE` events: `OPEN` (skipped when opened from an applied cut's edit
 *   link), `APPLY`, `CLOSE`, each with the SKU as `item_id`.
 *
 * Related: `unit-tests/shared/DefineCut.spec.tsx` (render only);
 * `components/Shared/DisplayDefineCut/DefineCutDisplay.tsx` (shows an applied cut and opens this
 * modal to edit it).
 */
import React, { useState, useEffect } from "react";
import { Media } from "@ryerson/frontend.next";
import DefineCutModalMobile from "./DefineCutModalMobile";
import DefineCutModalDesktop from "./DefineCutModalDesktop";
import { DefineCutContent, DefineCutText } from "./DefineCutModalContent";
import { useApplication, Language } from "@ryerson/frontend.application";
import { recursiveDeepEqual } from "@components/Checkout/ShippingAndBilling/Utilities";
import {
	Attribute,
	determineProductDisplay,
	GoogleAnalyticsEventName,
	GoogleAnalyticsEventActionNames,
	MatchingProduct,
	ProcessingAttributes,
	ProcessingAttributesData,
	ProcessingOperation,
	Product,
	Tolerance,
	Processing,
	sendGAEvent,
	LanguageToLocale,
} from "@ryerson/frontend.common";
import { CutShape, Form } from "@ryerson/common.process-cut";
import { Option } from "@ryerson/frontend.form-controls";
import { LowerCaseLocales } from "@enums/local.enum";
import { ProductAttribute } from "@ryerson/frontend.e-commerce";
import {
	CutAttribute,
	DefineCutParameters,
	GenericDropDownType,
	getCutAttributes,
	getListOfCutAttributes,
	getCutShape,
	getDefaultValuesForDefineCut,
	getFinish,
	getProcessing,
	getProcessingObject,
	getToleranceByProcess,
	getToleranceData,
	hasValidProperties,
	getProcessingName,
	AttributesName,
	parsePropertyMax,
} from "./DefineCutHelper";
import { defaultImage, getProduct3DRenderImage } from "@components/Helper/product3DRenderHelper";
import { APIUrl } from "@enums/apiUrl.enums";
import { UpdatedMatchingProduct } from "@components/Search/Helpers/Search.models";
import { NetworkOperationResult } from "@components/constants";
import { getAttributeName } from "@utils/translateAttributes";

/**
 * What Apply hands to the page through `onSuccessUpdate`.
 *
 * - `isResponseUpdated`: true when the cut or the quantity differs from what the modal was opened
 *   with. The comparison is a deep equality of the incoming `defineCutData` with the new cut
 *   before `id` is added, and of the `quantity` prop with the typed quantity, so a stored cut that
 *   has an `id` or numeric dimensions compares as changed.
 * - `index`: the product's own `index` when it has a non-zero one, otherwise the `index` prop.
 * - `defineCutResponse`: from `DefineCutHelper.ts` → `getProcessingObject`, with the incoming
 *   cut's `id` (undefined for a new cut).
 * - `processingAttribute` / `tolerances`: copied from the product details the modal used, `null`
 *   when absent.
 * - `quantity`: number of pieces.
 */
export type DefineCutResponse = {
	isResponseUpdated: boolean;
	index: number;
	ryersonSku: string;
	defineCutResponse: Processing;
	processingAttribute: ProcessingAttributes | null;
	tolerances: Tolerance[] | null;
	quantity: number;
};

/**
 * Props shared by `DefineCutModalDesktop.tsx` and `DefineCutModalMobile.tsx`. Every value and
 * handler comes from {@link DefineCutModal}; the views hold no cut state of their own.
 * `customSubtitle` is the product's part number display (`determineProductDisplay`).
 * `productDetailsAPIFetchStatus` hides the form while the product-details request is pending.
 */
export type DefineCutProps = {
	showDefineCutModal: boolean;
	handleClose: () => void;
	content: DefineCutText;
	product: Product | MatchingProduct;
	customSubtitle: string;
	cutAttributes: CutAttribute[];
	language: Language;
	updateInputValue: (event: any) => void;
	updateDropdownValue: (value: string, field: string) => void;
	defineCutParameters: DefineCutParameters;
	cutOptions: GenericDropDownType[];
	finishOptions: GenericDropDownType[];
	processOptions: GenericDropDownType[];
	toleranceOptions: GenericDropDownType[];
	handleApply: () => void;
	formSubmitted: boolean;
	validateCutParameters: () => void;
	imageSource: string;
	helpText: HelpText;
	productDetailsAPIFetchStatus: NetworkOperationResult;
	productCanBeCut: boolean;
};

/** Props of {@link DefineCutModal}. */
export type DefineCutModalProps = {
	/**
	 * Row of the line on the page; returned in {@link DefineCutResponse} unless the product
	 * has an `index`.
	 */
	index: number;
	/**
	 * When true (default), the modal fetches the product's details to get its processing
	 * attributes and tolerances. Pass false when `product` already carries them, as the product
	 * details page does.
	 */
	requireProductDetailForProcessing?: boolean;
	/** The line's current cut, or `{}` for a new cut. */
	defineCutData: Processing;
	showDefineCutModal: boolean;
	setShowDefineCutModal: React.Dispatch<React.SetStateAction<boolean>>;
	product: Product | MatchingProduct;
	/** Called on a valid Apply, before the modal closes. */
	onSuccessUpdate: (value: DefineCutResponse, index?: number) => void;
	/** The line's current number of pieces; prefills the quantity field. */
	quantity: number | string;
};

/** Tooltip text for the cut shape, process (from the selected tolerance) and finish dropdowns. */
type HelpText = {
	cutShape: string;
	process: string;
	finish: string;
};

/** The form with nothing entered; restored on Apply and on close. */
const emptyCutParameters = {
	width: "",
	length: "",
	outsideDiameter: "",
	insideDiameter: "",
	shortSide: "",
	longSide: "",
	oneSide: "",
	cutShape: {} as GenericDropDownType,
	finish: {} as GenericDropDownType,
	processing: {} as GenericDropDownType,
	tolerance: {} as GenericDropDownType,
	outsideTolerance: {} as GenericDropDownType,
	insideTolerance: {} as GenericDropDownType,
	oneSideTolerance: {} as GenericDropDownType,
	widthLengthTolerance: {} as GenericDropDownType,
	quantity: "",
};

/**
 * The cut-to-size modal for one product (see the file header for the flow and rules). Stays
 * mounted on the page; `showDefineCutModal` opens and closes it.
 */
const DefineCutModal: React.FC<DefineCutModalProps> = ({
	index,
	defineCutData = {} as Processing,
	showDefineCutModal,
	setShowDefineCutModal,
	product,
	onSuccessUpdate,
	requireProductDetailForProcessing = true,
	quantity = "",
}) => {
	const {
		localization: { language },
		axiosInstance,
	} = useApplication();
	const locale = LanguageToLocale[language];
	let customSubtitle = "";
	if (product) {
		customSubtitle = determineProductDisplay(product?.altPartNumber || "", product?.fastPath);
	}
	let defaultParameters: DefineCutParameters = {
		...emptyCutParameters,
		quantity,
	};

	const content: DefineCutText = DefineCutContent[language] || DefineCutContent.en;
	const [productDetail, setProductDetail] = useState<Product | MatchingProduct>({} as Product);
	const [productDetailsAPIFetchStatus, setProductDetailsAPIFetchStatus] =
		useState<NetworkOperationResult>(NetworkOperationResult.IDLE);
	const [processingData, setProcessingData] = useState<ProcessingAttributesData>(
		{} as ProcessingAttributesData
	);
	const [defineCutParameters, setDefineCutParameters] = useState<DefineCutParameters>({
		...emptyCutParameters,
		quantity,
	});
	const [cutShape, setCutShape] = useState<CutShape>("" as CutShape);
	const [process, setProcess] = useState<string>("");
	const [cutAttributes, setCutAttributes] = useState<CutAttribute[]>([]);
	const [productCanBeCut, setProductCanBeCut] = useState<boolean>(true);
	const [updateDefaultValue, setUpdateDefaultValue] = useState<boolean>(false);
	const [cutOptions, setCutOptions] = useState<GenericDropDownType[]>([]);
	const [processOptions, setProcessOptions] = useState<GenericDropDownType[]>([]);
	const [finishOptions, setFinishOptions] = useState<GenericDropDownType[]>([]);
	const [toleranceOptions, setToleranceOption] = useState<GenericDropDownType[]>([]);
	const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
	const [imageSource, setImageSource] = useState<string>(defaultImage);
	const [helpText, setHelpText] = useState<HelpText>({} as HelpText);

	// `updateCutAttributes` sets this flag for a new cut; defaults are applied after the new
	// field list and options are in state.
	useEffect(() => {
		if (updateDefaultValue) {
			getDefaultValuesForDefineCut(
				cutShape,
				process,
				productDetail,
				cutAttributes,
				cutOptions,
				processingData,
				defaultParameters,
				setDefineCutParameters,
				setUpdateDefaultValue,
				defineCutParameters
			);
		}
	}, [updateDefaultValue]);

	// Follows the page's quantity for the line while the modal is open.
	useEffect(() => {
		showDefineCutModal &&
			quantity &&
			setDefineCutParameters({ ...defineCutParameters, quantity });
	}, [quantity]);

	useEffect(() => {
		const toleranceDropDownOptions = getToleranceOptions(process);
		setToleranceOption([...toleranceDropDownOptions]);
	}, [process]);

	// Fetches the product's details (processing attributes, tolerances) on open, unless they are
	// already loaded for this product. With `requireProductDetailForProcessing` false the passed
	// product is used as is. A failed request is logged and sets the status to ERROR; neither
	// view has a message for that status.
	useEffect(() => {
		let requestBody = { altPartNumber: product?.altPartNumber || "" };
		if (
			showDefineCutModal &&
			requireProductDetailForProcessing &&
			product?.id !== productDetail?.id
		) {
			setProductDetailsAPIFetchStatus(NetworkOperationResult.PENDING);
			axiosInstance
				.post(`${APIUrl.PRODUCT_DETAILS}/${product?.id}`, requestBody)
				.then((response) => {
					const responseData = response?.data;
					if (responseData) {
						setProductDetail(responseData);
						setProductDetailsAPIFetchStatus(NetworkOperationResult.SUCCESS);
					}
				})
				.catch((e: any) => {
					console.error(`Failed to get Product detail ${product?.id}: ${e} `);
					setProductDetailsAPIFetchStatus(NetworkOperationResult.ERROR);
				});
		} else if (!requireProductDetailForProcessing && productDetail?.id !== product.id) {
			setProductDetail({ ...product });
		}
	}, [showDefineCutModal]);

	useEffect(() => {
		if (showDefineCutModal) {
			// Using localStorage as a workaround in the absence of shared context between this component
			// and the edit-cut links that set the flag:
			// `Shared/DisplayDefineCut/DefineCutDisplay.tsx`
			// and `Product/ProductDisplayDefineCut/ProductDisplayDefineCut.tsx`.
			if (localStorage.getItem("defineCutModalOpenFromEditCutClick") !== "true") {
				// only send open event if the modal was not opened from edit cut click
				sendGAEvent(GoogleAnalyticsEventName.CUT_TO_SIZE, {
					action: GoogleAnalyticsEventActionNames.OPEN,
					item_id: product?.materialMaster || "", // SKU
				});
			} else {
				// reset the flag if it was set to true
				localStorage.removeItem("defineCutModalOpenFromEditCutClick");
			}

			// Clears the previous product's details until the new product's details arrive.
			if (requireProductDetailForProcessing && product?.id !== productDetail?.id) {
				setProductDetail({} as Product);
			}
		}
	}, [showDefineCutModal]);

	// Once the product's details are in: build the dropdown options from its processing
	// attributes for the user's locale, then load the existing cut or apply new-cut defaults. No
	// processing attributes for this locale means the product cannot be cut here.
	useEffect(() => {
		setFormSubmitted(false);
		if (showDefineCutModal && productDetail.id) {
			const productProcessingData =
				productDetail?.processingAttribute &&
				(productDetail["processingAttribute"][
					LowerCaseLocales[language]
				] as ProcessingAttributesData);

			if (productProcessingData) {
				setProductCanBeCut(true);
				setProcessingData({ ...productProcessingData });
				const { processDropDownOption, finishDropDownOption, cutShapeDropDownOption } =
					updateAllDropdownBasedOnProcess({ ...productProcessingData });
				setProcessOptions([...processDropDownOption]);
				setFinishOptions([...finishDropDownOption]);
				setCutOptions([...cutShapeDropDownOption]);

				if (product?.id && Object.keys(defineCutData).length !== 0) {
					setCutShape(defineCutData?.shape as CutShape);
					setProcess(defineCutData?.process || "");

					const toleranceDropDownOptions = getToleranceOptions(
						defineCutData?.process || ""
					);
					setToleranceOption([...toleranceDropDownOptions]);
					updateCutAttributes(
						(defineCutData.shape as CutShape) || "",
						processDropDownOption,
						finishDropDownOption,
						cutShapeDropDownOption,
						toleranceDropDownOptions,
						defineCutData
					);
				} else {
					setDefineCutParameters({
						quantity: defineCutParameters?.quantity || quantity,
					} as DefineCutParameters);
					setProcess(productProcessingData?.processes[0]?.code || "");
					const toleranceDropDownOptions = getToleranceOptions(
						productProcessingData?.processes[0]?.code || ""
					);
					setToleranceOption([...toleranceDropDownOptions]);
					handleOnChangeCutShape(
						(productDetail?.form === Form.PLATE
							? (CutShape.RECTANGLE as CutShape)
							: (productProcessingData?.cutShapes[0]?.code as CutShape)) || "",
						processDropDownOption,
						finishDropDownOption,
						cutShapeDropDownOption,
						toleranceDropDownOptions
					);
				}
			} else {
				setCutAttributes([]);
				setProductCanBeCut(false);
			}
		}
	}, [productDetail.id, showDefineCutModal]);

	const isCircleOrRingShape = (value: string): boolean => value === "CIRCLE" || value === "RING";

	/** Shear operations, matched against the first three letters of a process code. */
	const shearProcesses = ["SHR", "STW", "STL"];

	useEffect(() => {
		if (cutShape && processOptions.length) {
			// should disable shear("SHR"), Shear to Width("STW"), Shear to Length("STL") when circle or ring cutShape is selected
			setProcessOptions(
				processOptions.map((option) => {
					const optionPrefix = option.value.slice(0, 3);
					return isCircleOrRingShape(cutShape) && shearProcesses.includes(optionPrefix)
						? { ...option, disabled: true }
						: { ...option, disabled: false };
				})
			);
		}
	}, [cutShape]);

	useEffect(() => {
		if (process && cutOptions.length) {
			// should disable circle and ring for shear process types shear("SHR"), Shear to Width("STW"), Shear to Length("STL")
			setCutOptions(
				cutOptions.map((option) => {
					const optionPrefix = process.slice(0, 3);
					return shearProcesses.includes(optionPrefix) &&
						isCircleOrRingShape(option.value)
						? { ...option, disabled: true }
						: { ...option, disabled: false };
				})
			);
		}
	}, [process]);

	// Based on Process, enable/disable the field.
	// Cut to length (CTL) and shear to length (STL) fix the width at the stock width; shear to
	// width (STW) fixes the length at the stock length. The fixed field is filled and disabled,
	// and is cleared and re-enabled when another process is chosen. Stock size here is
	// `parseFloat` of the attribute's unlocalized value, so the first size of a two-size value.
	// Validation reads the second size (`DefineCutHelper.ts` → `parsePropertyMax`), so for
	// `"240.0 IN/264.0 IN"` the field is fixed at 240 while validation checks against 264.
	// Rule: the shear-width lock should also use the longer size. Fixing the STW length at the
	// first size (240 above, where validation checks against 264) is a known defect.
	useEffect(() => {
		if (defineCutParameters?.processing?.value?.length) {
			let newCutAttributes = [...cutAttributes];
			let newDefineCutParameters: DefineCutParameters = { ...defineCutParameters };
			const productAttributes: (Attribute | ProductAttribute)[] =
				productDetail?.attributes as (Attribute | ProductAttribute)[];
			const productWidth = parseFloat(
				productAttributes?.find((attribute) => {
					const attributeName = getAttributeName(attribute, locale);
					return (
						attributeName.toLowerCase() ===
						content.cutAttributes.width.value.toLowerCase()
					);
				})?.value || ""
			).toString();

			const productLength = parseFloat(
				productAttributes?.find((attribute) => {
					const attributeName = getAttributeName(attribute, locale);
					return (
						attributeName.toLowerCase() ===
						content.cutAttributes.length.value.toLowerCase()
					);
				})?.value || ""
			).toString();

			const widthAttributeIndex = cutAttributes.findIndex(
				(cutAttribute) => cutAttribute.name === content.cutAttributes.width.value
			);
			const lengthAttributeIndex = cutAttributes.findIndex(
				(cutAttribute) => cutAttribute.name === content.cutAttributes.length.value
			);

			const hasWidth = widthAttributeIndex !== -1;
			const hasLength = lengthAttributeIndex !== -1;
			const widthDisabled = newCutAttributes[widthAttributeIndex]?.disabled;
			const lengthDisabled = newCutAttributes[lengthAttributeIndex]?.disabled;
			const processingValue = getProcessingName(newDefineCutParameters?.processing?.value);

			const changeWidthData = (index: number, value: string, disabled: boolean) => {
				newCutAttributes[index].disabled = disabled;
				newDefineCutParameters.width = value;
				setCutAttributes([...newCutAttributes]);
				setDefineCutParameters({ ...newDefineCutParameters });
			};

			const changeLengthData = (index: number, value: string, disabled: boolean) => {
				newCutAttributes[index].disabled = disabled;
				newDefineCutParameters.length = value;
				setCutAttributes([...newCutAttributes]);
				setDefineCutParameters({ ...newDefineCutParameters });
			};

			// Width functionality
			if (hasWidth && processingValue === ProcessingOperation.CutToLength && !widthDisabled) {
				changeWidthData(widthAttributeIndex, productWidth, true);
			} else if (
				hasWidth &&
				processingValue === ProcessingOperation.ShearToLength &&
				(newDefineCutParameters.width !== productWidth || !widthDisabled)
			) {
				changeWidthData(widthAttributeIndex, productWidth, true);
			} else if (
				hasWidth &&
				processingValue !== ProcessingOperation.ShearToLength &&
				processingValue !== ProcessingOperation.CutToLength &&
				widthDisabled
			) {
				changeWidthData(widthAttributeIndex, "", false);
			}

			// Length functionality
			if (
				hasLength &&
				processingValue === ProcessingOperation.ShearToWidth &&
				(newDefineCutParameters.length !== productLength || !lengthDisabled)
			) {
				changeLengthData(lengthAttributeIndex, productLength, true);
			} else if (
				hasLength &&
				processingValue !== ProcessingOperation.ShearToWidth &&
				lengthDisabled
			) {
				changeLengthData(lengthAttributeIndex, "", false);
			}
		}
	}, [defineCutParameters?.processing]);

	useEffect(() => {
		setImageSource(getProduct3DRenderImage(product, cutShape, locale));
	}, [cutShape, product, locale]);

	useEffect(() => {
		let foundTolerance = toleranceOptions.find(
			(option: GenericDropDownType) =>
				option.value === defineCutParameters?.oneSideTolerance?.value?.toString() ||
				option.value === defineCutParameters?.outsideTolerance?.value?.toString() ||
				option.value === defineCutParameters?.tolerance?.value?.toString() ||
				option.value === defineCutParameters?.widthLengthTolerance?.value?.toString() ||
				option.value === defineCutParameters?.insideTolerance?.value?.toString()
		);
		let foundFinish = finishOptions.find(
			(option: GenericDropDownType) => option.value === defineCutParameters?.finish?.value
		);

		let foundCutShape = cutOptions.find(
			(option: GenericDropDownType) => option.value === defineCutParameters?.cutShape?.value
		);

		let processingHelpText =
			foundTolerance?.helpText?.[LowerCaseLocales[language] as any] || "";
		let finishHelpText = foundFinish?.helpText || "";
		let cutShapeHelpText = foundCutShape?.helpText || "";

		setHelpText({
			cutShape: cutShapeHelpText,
			process: processingHelpText,
			finish: finishHelpText,
		});
	}, [defineCutParameters, toleranceOptions]);

	/**
	 * The product's tolerances for `process` as dropdown options, keyed `"min|max"` (see
	 * `DefineCutHelper.ts` header). All tolerance fields share this one list.
	 */
	const getToleranceOptions = (process: string) => {
		const toleranceByProcess = productDetail?.tolerances?.filter(
			(tolerance: Tolerance) => tolerance.processCode === process
		);
		const toleranceDropDownOptions: Option[] =
			toleranceByProcess?.map((tolerance: Tolerance) => {
				return {
					...tolerance,
					display: tolerance.displayValue,
					value: `${Number(tolerance.toleranceMin)?.toFixed(5)}|${Number(
						tolerance.toleranceMax
					)?.toFixed(5)}`,
				};
			}) || [];
		return toleranceDropDownOptions;
	};

	/**
	 * Cut shape, process and finish options from the product's processing attributes. Finish is
	 * optional, so its list starts with an empty "Select" option.
	 */
	const updateAllDropdownBasedOnProcess = (productProcessing: ProcessingAttributesData) => {
		const cutShapeDropDownOption: Option[] =
			productProcessing?.cutShapes?.map(
				({
					code,
					description,
					helpText,
				}: {
					code: string;
					description: string;
					helpText?: string;
				}) => ({
					value: code,
					display: description,
					helpText: helpText || null,
					disabled: false,
				})
			) || [];
		const processDropDownOption: Option[] =
			productProcessing?.processes?.map(
				({ code, description }: { code: string; description: string }) => ({
					value: code,
					display: description,
					disabled: false,
				})
			) || [];
		const finishDropDownOption: Option[] =
			productProcessing?.finishes?.map(
				({
					code,
					description,
					helpText,
				}: {
					code: string;
					description: string;
					helpText?: string;
				}) => ({
					value: code,
					display: description,
					helpText: helpText || null,
				})
			) || [];
		finishDropDownOption.unshift({ value: "", display: content.select }); //Finish is an optional parameter

		return {
			processDropDownOption,
			finishDropDownOption,
			cutShapeDropDownOption,
		};
	};

	/**
	 * Validates and, when every field passes and the quantity is above 0, hands the cut to the page
	 * through `onSuccessUpdate`, closes and resets the modal, and sends the GA `APPLY` event. In
	 * every case it marks the form submitted so the views show field errors. Does nothing when the
	 * product has no cut fields.
	 */
	const handleApply = () => {
		if (cutAttributes.length > 0) {
			if (validateCutParameters() && Number(defineCutParameters.quantity) > 0) {
				const mappedResponse: Processing = getProcessingObject(defineCutParameters);
				const response: DefineCutResponse = {
					index: (product as Product | UpdatedMatchingProduct)?.index || index,
					ryersonSku: product?.materialMaster,
					defineCutResponse: { ...mappedResponse, id: defineCutData.id },
					isResponseUpdated:
						!recursiveDeepEqual(defineCutData, mappedResponse) ||
						quantity !== defineCutParameters.quantity,
					processingAttribute: productDetail?.processingAttribute || null,
					tolerances: productDetail?.tolerances || null,
					quantity: Number(defineCutParameters.quantity),
				};
				onSuccessUpdate(response, index);
				setShowDefineCutModal(false);
				setDefineCutParameters(emptyCutParameters);
				setCutShape("" as CutShape);
				setProcess("");

				// send a google analytics event on apply cut button click
				sendGAEvent(GoogleAnalyticsEventName.CUT_TO_SIZE, {
					action: GoogleAnalyticsEventActionNames.APPLY,
					item_id: product?.materialMaster || "",
				});
			}
			setFormSubmitted(true);
		}
	};

	/** Closes without saving, resets the form, and sends the GA `CLOSE` event. */
	const handleClose = () => {
		setShowDefineCutModal(false);
		setDefineCutParameters(emptyCutParameters);
		setCutShape("" as CutShape);
		setProcess("");

		// send a google analytics event on modal close
		sendGAEvent(GoogleAnalyticsEventName.CUT_TO_SIZE, {
			action: GoogleAnalyticsEventActionNames.CLOSE,
			item_id: product?.materialMaster || "",
		});
	};

	/**
	 * Checks every cut field, writes `isValid` / `error` onto each {@link CutAttribute}, and
	 * returns true when all pass. Every dimension field is required. On top of that:
	 * - width, length: `DefineCutHelper.ts` → `hasValidProperties` for the product's form.
	 * - square side (`oneSide`) and outside diameter: the stock plate width, or at most that width
	 *   minus 2 inches.
	 * - outside diameter: at least 3 inches for oxy-fuel (`BRN`) and plasma (`PFC`) processes, at
	 *   least 1 inch for laser (`LZR`).
	 * - inside diameter: less than the outside diameter, then `hasValidProperties` (for a plate
	 *   ring, the wall rule in `DefineCutHelper.ts` → `odAndIdDifferenceValid`).
	 * Quantity is checked by `handleApply`, not here. The views also call this as each input's
	 * `validation` callback.
	 */
	const validateCutParameters = () => {
		let hasErrors: boolean = false;
		let plateWidth: number = parsePropertyMax(productDetail, "width", language);

		cutAttributes.forEach((cutAttribute: CutAttribute) => {
			if (cutAttribute.name === content.cutAttributes.width.value) {
				const hasValidWidth = hasValidProperties(
					productDetail,
					defineCutParameters,
					cutAttribute.name.toLowerCase() as keyof DefineCutParameters,
					content,
					language
				);
				if (defineCutParameters.width === "" || !hasValidWidth.isValid) {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = !hasValidWidth.isValid ? hasValidWidth.errorMsg : "";
				} else {
					cutAttribute.isValid = true;
					cutAttribute.error = "";
				}
			} else if (cutAttribute.name === content.cutAttributes.length.value) {
				const hasValidLength = hasValidProperties(
					productDetail,
					defineCutParameters,
					cutAttribute.name.toLowerCase() as keyof DefineCutParameters,
					content,
					language
				);
				if (defineCutParameters.length === "" || !hasValidLength.isValid) {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = !hasValidLength.isValid ? hasValidLength.errorMsg : "";
				} else {
					cutAttribute.isValid = true;
					cutAttribute.error = "";
				}
			} else if (cutAttribute.name === content.cutAttributes.oneSide.value) {
				if (defineCutParameters.oneSide === "") {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = "";
				} else if (
					parseFloat(defineCutParameters.oneSide) > plateWidth - 2 &&
					parseFloat(defineCutParameters.oneSide) !== plateWidth
				) {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = content.errors.oneSide.lessThanPlateWidth;
				} else {
					cutAttribute.isValid = true;
					cutAttribute.error = "";
				}
			} else if (cutAttribute.name === content.cutAttributes.insideDiameter.value) {
				const hasValidInsideDiameter = hasValidProperties(
					productDetail,
					defineCutParameters,
					cutAttribute.name as keyof DefineCutParameters,
					content,
					language
				);
				if (defineCutParameters.insideDiameter === "") {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = "";
				} else if (
					parseFloat(defineCutParameters.insideDiameter) >=
					parseFloat(defineCutParameters.outsideDiameter)
				) {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = content.errors.insideDiameter.lessThanOutside;
				} else if (!hasValidInsideDiameter.isValid) {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = hasValidInsideDiameter.errorMsg;
				} else {
					cutAttribute.isValid = true;
					cutAttribute.error = "";
				}
			} else if (cutAttribute.name === content.cutAttributes.outsideDiameter.value) {
				if (defineCutParameters.outsideDiameter === "") {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = "";
				} else if (
					parseFloat(defineCutParameters.outsideDiameter) > plateWidth - 2 &&
					parseFloat(defineCutParameters.outsideDiameter) !== plateWidth
				) {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = content.errors.outsideDiameter.lessThanPlateWidth;
				} else if (
					(getProcessingName(defineCutParameters.processing.value) ===
						ProcessingOperation.OxyFuelBurning ||
						getProcessingName(defineCutParameters.processing.value) ===
							ProcessingOperation.Plasma) &&
					parseFloat(defineCutParameters.outsideDiameter) < 3
				) {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = content.errors.outsideDiameter.minimumForOxyFuel;
				} else if (
					getProcessingName(defineCutParameters.processing.value) ===
						ProcessingOperation.Laser &&
					parseFloat(defineCutParameters.outsideDiameter) < 1
				) {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = content.errors.outsideDiameter.minimumForLaserCut;
				} else {
					cutAttribute.isValid = true;
					cutAttribute.error = "";
				}
			} else if (cutAttribute.name === content.cutAttributes.longSide.value) {
				if (defineCutParameters.longSide === "") {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = "";
				} else {
					cutAttribute.isValid = true;
					cutAttribute.error = "";
				}
			} else if (cutAttribute.name === content.cutAttributes.shortSide.value) {
				if (defineCutParameters.shortSide === "") {
					hasErrors = true;
					cutAttribute.isValid = false;
					cutAttribute.error = "";
				} else {
					cutAttribute.isValid = true;
					cutAttribute.error = "";
				}
			}
		});

		setCutAttributes([...cutAttributes]);
		return !hasErrors;
	};

	/**
	 * Rebuilds the field list for cut shape `value`. With an existing cut (`defineCutData`), fills
	 * the form from it: `shortSide` from `width`, `longSide` from `length`, `oneSide` from `width`
	 * or `length`, and every tolerance field from the same `toleranceMin` / `toleranceMax` pair.
	 * Without one, sets `updateDefaultValue` so the defaults effect resets the form.
	 */
	const updateCutAttributes = (
		value: CutShape,
		processOptions: GenericDropDownType[],
		finishOptions: GenericDropDownType[],
		cutOptions: GenericDropDownType[],
		toleranceOptions: GenericDropDownType[],
		defineCutData?: Processing
	) => {
		let newCutAttributes: CutAttribute[] = getCutAttributes(language, product, value);
		setCutAttributes([...newCutAttributes]);
		if (defineCutData && Object.keys(defineCutData).length > 0) {
			const existingParameters: DefineCutParameters = {
				...defaultParameters,
			};

			newCutAttributes.forEach((cutAttribute: CutAttribute) => {
				if (cutAttribute.name === AttributesName.Width) {
					existingParameters.width = defineCutData.width?.toString() || "";
				} else if (cutAttribute.name === AttributesName.Length) {
					existingParameters.length = defineCutData.length?.toString() || "";
				} else if (cutAttribute.name === AttributesName.OutsideDiameter) {
					existingParameters.outsideDiameter =
						defineCutData.outsideDiameter?.toString() || "";
				} else if (cutAttribute.name === AttributesName.InsideDiameter) {
					existingParameters.insideDiameter =
						defineCutData.insideDiameter?.toString() || "";
				} else if (cutAttribute.name === AttributesName.Processing) {
					existingParameters.processing = getProcessing(
						processOptions,
						defineCutData?.process
					);
				} else if (cutAttribute.name === AttributesName.Finish) {
					existingParameters.finish = getFinish(finishOptions, defineCutData.finish);
				} else if (cutAttribute.name === AttributesName.CutShape) {
					existingParameters.cutShape = getCutShape(cutOptions, defineCutData.shape);
				} else if (cutAttribute.name === AttributesName.ShortSide) {
					existingParameters.shortSide = defineCutData.width?.toString() || "";
				} else if (cutAttribute.name === AttributesName.LongSide) {
					existingParameters.longSide = defineCutData.length?.toString() || "";
				} else if (cutAttribute.name === AttributesName.OneSide) {
					existingParameters.oneSide =
						defineCutData.width?.toString() || defineCutData.length?.toString() || "";
				} else if (cutAttribute.name === AttributesName.Tolerance) {
					existingParameters.tolerance = getToleranceData(
						toleranceOptions,
						`${Number(defineCutData.toleranceMin)?.toFixed(5)?.toString() || ""}|${
							Number(defineCutData.toleranceMax)?.toFixed(5)?.toString() || ""
						}`
					);
				} else if (cutAttribute.name === AttributesName.OutsideTolerance) {
					existingParameters.outsideTolerance = getToleranceData(
						toleranceOptions,
						`${Number(defineCutData.toleranceMin)?.toFixed(5)?.toString() || ""}|${
							Number(defineCutData.toleranceMax)?.toFixed(5)?.toString() || ""
						}`
					);
				} else if (cutAttribute.name === AttributesName.InsideTolerance) {
					existingParameters.insideTolerance = getToleranceData(
						toleranceOptions,
						`${Number(defineCutData.toleranceMin)?.toFixed(5)?.toString() || ""}|${
							Number(defineCutData.toleranceMax)?.toFixed(5)?.toString() || ""
						}`
					);
				} else if (cutAttribute.name === AttributesName.OneSideTolerance) {
					existingParameters.oneSideTolerance = getToleranceData(
						toleranceOptions,
						`${Number(defineCutData.toleranceMin)?.toFixed(5)?.toString() || ""}|${
							Number(defineCutData.toleranceMax)?.toFixed(5)?.toString() || ""
						}`
					);
				} else if (cutAttribute.name === AttributesName.WidthLengthTolerance) {
					existingParameters.widthLengthTolerance = getToleranceData(
						toleranceOptions,
						`${Number(defineCutData.toleranceMin)?.toFixed(5)?.toString() || ""}|${
							Number(defineCutData.toleranceMax)?.toFixed(5)?.toString() || ""
						}`
					);
				}
			});

			setDefineCutParameters({ ...existingParameters });
		} else {
			setUpdateDefaultValue(true);
		}
	};

	const handleOnChangeCutShape = (
		value: string,
		processOptions: GenericDropDownType[],
		finishOptions: GenericDropDownType[],
		cutOptions: GenericDropDownType[],
		toleranceOptions: GenericDropDownType[],
		defineCutData?: Processing
	) => {
		updateCutAttributes(
			value as CutShape,
			processOptions,
			finishOptions,
			cutOptions,
			toleranceOptions,
			defineCutData
		);
		setCutShape(value as CutShape);
	};

	/** Selects process `value` and resets every tolerance field to that process's default. */
	const handleOnChangeProcess = (value: string) => {
		const listOfCutAttributes = getListOfCutAttributes(cutAttributes);
		let currentDefinedCutData = {
			...defineCutParameters,
			processing: getProcessing(processOptions, value),
			...getToleranceByProcess(productDetail, listOfCutAttributes, value),
		};

		setProcess(value);
		setDefineCutParameters({ ...currentDefinedCutData });
	};

	/**
	 * Stores a typed value under the input's `name`, which is a {@link DefineCutParameters} key.
	 */
	const updateInputValue = (event: any) => {
		setDefineCutParameters((prev: DefineCutParameters): DefineCutParameters => {
			prev[event.target.name as keyof DefineCutParameters] = event.target.value;
			return {
				...prev,
			};
		});
	};

	/**
	 * Handles a dropdown change for `field` (an `AttributesName` value). A new cut shape rebuilds
	 * the fields and resets the form; a new process resets the tolerances; finish and tolerance
	 * changes only store the selection.
	 */
	const updateDropdownValue = (value: string, field: string) => {
		let updateData = { ...defineCutParameters };
		if (field === AttributesName.CutShape) {
			handleOnChangeCutShape(
				value,
				processOptions,
				finishOptions,
				cutOptions,
				toleranceOptions
			);
		} else if (field === AttributesName.Processing) {
			handleOnChangeProcess(value);
		} else {
			if (field === AttributesName.Finish) {
				updateData.finish = getFinish(finishOptions, value);
			}

			if (field === AttributesName.Tolerance) {
				updateData.tolerance = getToleranceData(toleranceOptions, value);
			}

			if (field === AttributesName.OutsideTolerance) {
				updateData.outsideTolerance = getToleranceData(toleranceOptions, value);
			}

			if (field === AttributesName.InsideTolerance) {
				updateData.insideTolerance = getToleranceData(toleranceOptions, value);
			}

			if (field === AttributesName.OneSideTolerance) {
				updateData.oneSideTolerance = getToleranceData(toleranceOptions, value);
			}

			if (field === AttributesName.WidthLengthTolerance) {
				updateData.widthLengthTolerance = getToleranceData(toleranceOptions, value);
			}

			setDefineCutParameters({ ...updateData });
		}
	};

	return (
		<>
			<Media lessThan="lg">
				<DefineCutModalMobile
					showDefineCutModal={showDefineCutModal}
					handleClose={handleClose}
					content={content}
					product={product}
					customSubtitle={customSubtitle}
					cutAttributes={cutAttributes}
					language={language}
					defineCutParameters={defineCutParameters}
					updateInputValue={updateInputValue}
					updateDropdownValue={updateDropdownValue}
					cutOptions={cutOptions}
					finishOptions={finishOptions}
					processOptions={processOptions}
					toleranceOptions={toleranceOptions}
					handleApply={handleApply}
					formSubmitted={formSubmitted}
					validateCutParameters={validateCutParameters}
					imageSource={imageSource}
					helpText={helpText}
					productDetailsAPIFetchStatus={productDetailsAPIFetchStatus}
					productCanBeCut={productCanBeCut}
				/>
			</Media>
			<Media greaterThanOrEqual="lg">
				<DefineCutModalDesktop
					showDefineCutModal={showDefineCutModal}
					handleClose={handleClose}
					content={content}
					product={product}
					customSubtitle={customSubtitle}
					cutAttributes={cutAttributes}
					language={language}
					defineCutParameters={defineCutParameters}
					updateInputValue={updateInputValue}
					updateDropdownValue={updateDropdownValue}
					cutOptions={cutOptions}
					finishOptions={finishOptions}
					processOptions={processOptions}
					toleranceOptions={toleranceOptions}
					handleApply={handleApply}
					formSubmitted={formSubmitted}
					validateCutParameters={validateCutParameters}
					imageSource={imageSource}
					helpText={helpText}
					productDetailsAPIFetchStatus={productDetailsAPIFetchStatus}
					productCanBeCut={productCanBeCut}
				/>
			</Media>
		</>
	);
};

export default DefineCutModal;
